/**
 * Player Adapter
 *
 * Converts between PlayerBlock (block system) and Player (canvas).
 * Provides bidirectional conversion for both reads and mutations.
 */

import type { PlayerBlock } from '@/types/blocks'
import type { Player } from '@/types/Player.types'
import { PlayerPropertiesSchema } from '@/types/BlockProperties'
import { extractPointFromProperties } from '@/utils/Block.utils'

/**
 * Convert a PlayerBlock to a canvas Player.
 *
 * @param playerBlock - The block representation of a player
 * @returns Canvas Player object for rendering
 * @throws Error if playerBlock or properties are null/undefined
 * @throws ZodError if properties validation fails
 */
export function playerBlockToPlayer(playerBlock: PlayerBlock): Player {
	// Guard against null/undefined input
	if (!playerBlock || !playerBlock.properties) {
		console.error('[playerAdapter] Received invalid PlayerBlock:', playerBlock)
		throw new Error(`Invalid PlayerBlock: block or properties is null/undefined`)
	}

	// Validate properties with better error context using safeParse
	const parseResult = PlayerPropertiesSchema.safeParse(playerBlock.properties)
	if (!parseResult.success) {
		console.error('[playerAdapter] Invalid player properties:', {
			blockId: playerBlock.id,
			properties: playerBlock.properties,
			issues: parseResult.error.issues
		})
		throw parseResult.error
	}

	const props = parseResult.data

	// Spatial properties accessed from properties (Phase 6 migration)
	const pointData = extractPointFromProperties(playerBlock.properties)

	// Use raw block ID - type information is already in the Player object itself
	// No prefixing needed (e.g., just "uuid" not "player-uuid")
	return {
		id: playerBlock.id,
		x: pointData?.x ?? 0,
		y: pointData?.y ?? 0,
		label: props.label,
		color: props.color,
		isLineman: props.isLineman,
		isGhost: props.isGhost,
		leverageShading: props.leverageShading ?? 'none',
		// sourcePlayerId would be set if there's a player_template reference
		// This requires block_references lookup, handled separately
	}
}

/**
 * Partial Player update from canvas (position, label, color, etc.)
 */
export interface PlayerUpdate {
	x?: number
	y?: number
	label?: string
	color?: string
	isLineman?: boolean
	isGhost?: boolean
	leverageShading?: 'left' | 'right' | 'none'
}

/**
 * Partial PlayerBlock update for mutations.
 */
export interface PlayerBlockUpdate {
	position_x?: number
	position_y?: number
	player_label?: string
	player_color?: string
	player_is_lineman?: boolean
	player_is_ghost?: boolean
	player_leverage_shading?: 'left' | 'right' | 'none'
}

/**
 * Convert a canvas Player update to a block update.
 * Only includes fields that are present in the update.
 *
 * @param update - Partial Player update from canvas
 * @returns Partial PlayerBlock update for mutation
 */
export function playerToBlockUpdate(update: PlayerUpdate): PlayerBlockUpdate {
	const blockUpdate: PlayerBlockUpdate = {}

	if (update.x !== undefined) {
		blockUpdate.position_x = update.x
	}
	if (update.y !== undefined) {
		blockUpdate.position_y = update.y
	}
	if (update.label !== undefined) {
		blockUpdate.player_label = update.label
	}
	if (update.color !== undefined) {
		blockUpdate.player_color = update.color
	}
	if (update.isLineman !== undefined) {
		blockUpdate.player_is_lineman = update.isLineman
	}
	if (update.isGhost !== undefined) {
		blockUpdate.player_is_ghost = update.isGhost
	}
	if (update.leverageShading !== undefined) {
		blockUpdate.player_leverage_shading = update.leverageShading
	}

	return blockUpdate
}

/**
 * Convert an array of PlayerBlocks to canvas Players.
 *
 * @param playerBlocks - Array of PlayerBlock objects
 * @returns Array of canvas Player objects
 */
export function playerBlocksToPlayers(playerBlocks: PlayerBlock[]): Player[] {
	return playerBlocks
		.filter((block): block is PlayerBlock =>
			block != null &&
			block.properties != null &&
			typeof block.properties === 'object'
		)
		.map(playerBlockToPlayer)
}

/**
 * Convert a canvas Player to a PlayerBlock for the block rendering system.
 * Used by read-only renderers (PlayersOverlay, thumbnails) to feed Player data
 * into the BlockRenderer pipeline.
 */
export function playerToPlayerBlock(player: Player): PlayerBlock {
	return {
		id: player.id,
		type: 'player',
		parent_block_id: null,
		root_block_id: player.id,
		content: [],
		created_by: '00000000-0000-0000-0000-000000000000',
		created_at: new Date(),
		updated_at: new Date(),
		properties: {
			x: player.x,
			y: player.y,
			coordinateSystem: 'field',
			label: player.label,
			color: player.color,
			isLineman: player.isLineman ?? false,
			isGhost: player.isGhost ?? false,
			hashRelative: false,
			leverageShading: 'none',
		},
	}
}

/**
 * Convert an array of canvas Players to PlayerBlocks.
 */
export function playersToPlayerBlocks(players: Player[]): PlayerBlock[] {
	return players.map(playerToPlayerBlock)
}

/**
 * Create a synthetic PlayerBlock from position + display info.
 * Used by animation overlays and thumbnails where full Player objects aren't available.
 */
export function createPlayerBlock(
	id: string,
	position: { x: number; y: number },
	info: { label: string; color: string } | undefined,
	overrides?: { isGhost?: boolean },
): PlayerBlock {
	return {
		id: overrides?.isGhost ? `${id}-ghost` : id,
		type: 'player',
		parent_block_id: null,
		root_block_id: id,
		content: [],
		created_by: '00000000-0000-0000-0000-000000000000',
		created_at: new Date(),
		updated_at: new Date(),
		properties: {
			x: position.x,
			y: position.y,
			coordinateSystem: 'field',
			label: overrides?.isGhost ? '' : (info?.label ?? id),
			color: info?.color ?? '#FFFFFF',
			isLineman: false,
			isGhost: overrides?.isGhost ?? false,
			hashRelative: false,
			leverageShading: 'none',
		},
	}
}
